/**
 * UI Store
 * Manages UI state including panel visibility, theme, modals, and user preferences
 * Uses Zustand persist middleware to save preferences to localStorage
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * App mode types
 */
export type AppMode = 'workbench' | 'history' | 'sequencer';

/**
 * Right panel tab types
 */
export type RightPanelTab = 'search' | 'recs' | 'playlist';

/**
 * Theme options
 */
export type Theme = 'dark' | 'light' | 'system';

/**
 * View mode for track list
 */
export type TrackListView = 'compact' | 'comfortable' | 'detailed';

/**
 * UI state interface
 */
export interface UIState {
  // App mode
  mode: AppMode;
  setMode: (mode: AppMode) => void;

  // Panel visibility
  leftSidebarVisible: boolean;
  rightPanelVisible: boolean;
  discoveryPanelOpen: boolean; // Preserved from existing
  toggleLeftSidebar: () => void;
  toggleRightPanel: () => void;
  toggleDiscoveryPanel: () => void; // Preserved from existing
  setLeftSidebarVisible: (visible: boolean) => void;
  setRightPanelVisible: (visible: boolean) => void;
  setDiscoveryPanelOpen: (open: boolean) => void; // Preserved from existing

  // Active tabs
  rightPanelTab: RightPanelTab;
  setRightPanelTab: (tab: RightPanelTab) => void;

  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  // Track list view mode
  trackListView: TrackListView;
  setTrackListView: (view: TrackListView) => void;

  // Modals
  reorderModalOpen: boolean;
  setReorderModalOpen: (open: boolean) => void;
  smartSlotModalOpen: boolean;
  setSmartSlotModalOpen: (open: boolean) => void;
  exportModalOpen: boolean;
  setExportModalOpen: (open: boolean) => void;
  settingsModalOpen: boolean;
  setSettingsModalOpen: (open: boolean) => void;

  // Preferences
  autoSave: boolean;
  setAutoSave: (enabled: boolean) => void;
  showWaveforms: boolean;
  setShowWaveforms: (enabled: boolean) => void;
  showKeyNotation: 'camelot' | 'standard' | 'both';
  setShowKeyNotation: (notation: 'camelot' | 'standard' | 'both') => void;

  // Keyboard shortcuts helper
  shortcutsVisible: boolean;
  toggleShortcuts: () => void;

  // Toast notifications (managed in UI)
  toastMessage: string | null;
  toastType: 'info' | 'success' | 'warning' | 'error' | null;
  showToast: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void;
  clearToast: () => void;

  // Discovery panel preferences
  selectedPlaylistIds: string[];
  setSelectedPlaylistIds: (ids: string[]) => void;

  // Reset to defaults
  resetUI: () => void;
}

/**
 * Default UI state
 */
const defaultState = {
  mode: 'workbench' as AppMode,
  leftSidebarVisible: true,
  rightPanelVisible: true,
  discoveryPanelOpen: true,
  rightPanelTab: 'search' as RightPanelTab,
  theme: 'dark' as Theme,
  trackListView: 'comfortable' as TrackListView,
  reorderModalOpen: false,
  smartSlotModalOpen: false,
  exportModalOpen: false,
  settingsModalOpen: false,
  autoSave: false,
  showWaveforms: false,
  showKeyNotation: 'camelot' as const,
  shortcutsVisible: false,
  toastMessage: null,
  toastType: null,
  selectedPlaylistIds: [] as string[],
};

/**
 * UI store with persistence
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      ...defaultState,

      // App mode
      setMode: (mode) => set({ mode }),

      // Panel visibility
      toggleLeftSidebar: () =>
        set({ leftSidebarVisible: !get().leftSidebarVisible }),

      toggleRightPanel: () =>
        set({ rightPanelVisible: !get().rightPanelVisible }),

      toggleDiscoveryPanel: () =>
        set({ discoveryPanelOpen: !get().discoveryPanelOpen }),

      setLeftSidebarVisible: (visible) =>
        set({ leftSidebarVisible: visible }),

      setRightPanelVisible: (visible) =>
        set({ rightPanelVisible: visible }),

      setDiscoveryPanelOpen: (open) =>
        set({ discoveryPanelOpen: open }),

      // Active tabs
      setRightPanelTab: (tab) =>
        set({ rightPanelTab: tab }),

      // Theme
      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document root for CSS variables
        if (theme === 'system') {
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          document.documentElement.classList.toggle('dark', prefersDark);
        } else {
          document.documentElement.classList.toggle('dark', theme === 'dark');
        }
      },

      toggleTheme: () => {
        const { theme } = get();
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        get().setTheme(newTheme);
      },

      // Track list view
      setTrackListView: (view) =>
        set({ trackListView: view }),

      // Modals
      setReorderModalOpen: (open) =>
        set({ reorderModalOpen: open }),

      setSmartSlotModalOpen: (open) =>
        set({ smartSlotModalOpen: open }),

      setExportModalOpen: (open) =>
        set({ exportModalOpen: open }),

      setSettingsModalOpen: (open) =>
        set({ settingsModalOpen: open }),

      // Preferences
      setAutoSave: (enabled) =>
        set({ autoSave: enabled }),

      setShowWaveforms: (enabled) =>
        set({ showWaveforms: enabled }),

      setShowKeyNotation: (notation) =>
        set({ showKeyNotation: notation }),

      // Keyboard shortcuts
      toggleShortcuts: () =>
        set({ shortcutsVisible: !get().shortcutsVisible }),

      // Toast notifications
      showToast: (message, type) =>
        set({ toastMessage: message, toastType: type }),

      clearToast: () =>
        set({ toastMessage: null, toastType: null }),

      // Discovery panel preferences
      setSelectedPlaylistIds: (ids) =>
        set({ selectedPlaylistIds: ids }),

      // Reset to defaults
      resetUI: () => set(defaultState),
    }),
    {
      name: 'splister-ui-preferences', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist user preferences, not transient state
      partialize: (state) => ({
        mode: state.mode,
        leftSidebarVisible: state.leftSidebarVisible,
        rightPanelVisible: state.rightPanelVisible,
        discoveryPanelOpen: state.discoveryPanelOpen,
        rightPanelTab: state.rightPanelTab,
        theme: state.theme,
        trackListView: state.trackListView,
        autoSave: state.autoSave,
        showWaveforms: state.showWaveforms,
        showKeyNotation: state.showKeyNotation,
        selectedPlaylistIds: state.selectedPlaylistIds,
      }),
    }
  )
);

// Initialize theme on load
if (typeof window !== 'undefined') {
  const storedTheme = useUIStore.getState().theme;
  if (storedTheme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.classList.toggle('dark', prefersDark);
  } else {
    document.documentElement.classList.toggle('dark', storedTheme === 'dark');
  }

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (useUIStore.getState().theme === 'system') {
      document.documentElement.classList.toggle('dark', e.matches);
    }
  });
}
